<template>
  <div class="cv-scope">
    <b-form-row>
      <!-- Programs: chosen by UMR; each chosen UMR opens to tick or untick its LimeIQ keys. -->
      <b-col lg="4" class="mb-3">
        <section
          ref="programs"
          tabindex="-1"
          aria-labelledby="cv-scope-programs"
          class="cv-scope__tile"
          :class="tileClass('programs', ['program_umrs', 'program_key_exceptions', 'applicable_keys'])"
        >
          <h4 id="cv-scope-programs" class="cv-scope__title">Programs</h4>
          <b-form-radio-group
            :checked="value.programsMode"
            :options="modeOptions"
            buttons
            button-variant="outline-primary"
            size="sm"
            name="cv-scope-programs-mode"
            class="cv-scope__modes"
            @change="set('programsMode', $event)"
          />
          <div v-if="loading" class="small text-muted mt-2">
            <cv-spinner class="mr-1" />
            Loading {{ companyName }}'s programs...
          </div>
          <p v-else-if="value.programsMode === 'all'" class="small text-muted mt-2 mb-0">
            Every program {{ companyName }} leads.
          </p>
          <template v-else>
            <div class="small text-muted mt-2 mb-1">
              {{ value.programsMode === 'only' ? 'Only these UMRs:' : `All programs ${companyName} leads, except:` }}
            </div>
            <!-- Adds a UMR; the chosen ones are listed below, so this box stays one line. -->
            <el-select
              v-model="adding"
              filterable
              remote
              :remote-method="searchUmrs"
              size="small"
              placeholder="Search UMRs or LimeIQ keys to add"
              class="cv-scope__select"
              @focus="searchUmrs('')"
              @change="addUmr"
            >
              <el-option
                v-for="group in umrMatches"
                :key="group.umr"
                :label="group.umr"
                :value="group.umr"
                :disabled="value.umrs.includes(group.umr)"
              >
                <span>{{ group.umr }}</span>
                <span class="cv-scope__note">{{ plural(group.programs.length, 'key') }}</span>
              </el-option>
            </el-select>
            <!-- The chosen UMRs, one open at a time, in a list of fixed height. -->
            <div v-if="value.umrs.length" class="cv-scope__chosen">
              <div v-for="umr in value.umrs" :key="umr" class="cv-scope__umr">
                <div class="cv-scope__umr-row">
                  <button
                    type="button"
                    class="cv-scope__umr-toggle"
                    :aria-expanded="isOpen(umr) ? 'true' : 'false'"
                    @click="toggleOpen(umr)"
                  >
                    <b-icon :icon="isOpen(umr) ? 'chevron-down' : 'chevron-right'" font-scale="0.8" class="mr-1" />
                    <span class="cv-scope__mono">{{ umr }}</span>
                    <span class="cv-scope__note">{{ keysLine(umr) }}</span>
                  </button>
                  <button type="button" class="cv-scope__remove" :aria-label="`Remove ${umr}`" @click="removeUmr(umr)">
                    <b-icon icon="x" />
                  </button>
                </div>
                <div v-if="isOpen(umr)" class="cv-scope__keys">
                  <p class="small text-muted mb-1">
                    {{ value.programsMode === 'only' ? 'Untick a key to leave it out.' : 'Untick a key to keep checking it.' }}
                  </p>
                  <b-form-checkbox
                    v-for="program in programsOf(umr)"
                    :key="program.key"
                    :checked="!isException(program.key)"
                    class="cv-scope__key"
                    @change="setKeyTicked(program.key, $event)"
                  >
                    <span class="cv-scope__mono" :title="program.key">{{ shortKey(program.key, umr) }}</span>
                    <span class="cv-scope__note">{{ programDetails(program) }}</span>
                  </b-form-checkbox>
                </div>
              </div>
            </div>
          </template>
          <div v-if="errorOf(['program_umrs', 'program_key_exceptions', 'applicable_keys'])" class="cv-scope__error">
            {{ errorOf(['program_umrs', 'program_key_exceptions', 'applicable_keys']) }}
          </div>
        </section>
      </b-col>

      <!-- Jurisdictions: every jurisdiction, matched on the program's own. -->
      <b-col lg="4" class="mb-3">
        <section
          ref="jurisdictions"
          tabindex="-1"
          aria-labelledby="cv-scope-jurisdictions"
          class="cv-scope__tile"
          :class="tileClass('jurisdictions', ['jurisdictions'])"
        >
          <h4 id="cv-scope-jurisdictions" class="cv-scope__title">Jurisdictions</h4>
          <b-form-radio-group
            :checked="value.jurisdictionsMode"
            :options="modeOptions"
            buttons
            button-variant="outline-primary"
            size="sm"
            name="cv-scope-jurisdictions-mode"
            class="cv-scope__modes"
            @change="set('jurisdictionsMode', $event)"
          />
          <p v-if="value.jurisdictionsMode === 'all'" class="small text-muted mt-2 mb-0">Claims in every jurisdiction.</p>
          <template v-else>
            <div class="small text-muted mt-2 mb-1">
              {{ value.jurisdictionsMode === 'only' ? 'Only claims in:' : 'All claims, except in:' }}
            </div>
            <el-select
              :value="value.jurisdictions"
              multiple
              filterable
              size="small"
              placeholder="Search jurisdictions"
              class="cv-scope__select"
              @input="set('jurisdictions', $event)"
            >
              <el-option v-for="name in jurisdictionOptions" :key="name" :label="name" :value="name" />
            </el-select>
            <p class="small text-muted mt-1 mb-0">A program with no jurisdiction recorded is still checked.</p>
          </template>
          <div v-if="errorOf(['jurisdictions'])" class="cv-scope__error">{{ errorOf(['jurisdictions']) }}</div>
        </section>
      </b-col>

      <!-- TPAs: the TPA on each program. -->
      <b-col lg="4" class="mb-3">
        <section
          ref="tpas"
          tabindex="-1"
          aria-labelledby="cv-scope-tpas"
          class="cv-scope__tile"
          :class="tileClass('tpas', ['tpas'])"
        >
          <h4 id="cv-scope-tpas" class="cv-scope__title">TPAs</h4>
          <b-form-radio-group
            :checked="value.tpasMode"
            :options="modeOptions"
            buttons
            button-variant="outline-primary"
            size="sm"
            name="cv-scope-tpas-mode"
            class="cv-scope__modes"
            @change="set('tpasMode', $event)"
          />
          <p v-if="value.tpasMode === 'all'" class="small text-muted mt-2 mb-0">Claims handled by any TPA.</p>
          <template v-else>
            <div class="small text-muted mt-2 mb-1">
              {{ value.tpasMode === 'only' ? 'Only claims handled by:' : 'All claims, except those handled by:' }}
            </div>
            <el-select
              :value="value.tpas"
              multiple
              filterable
              size="small"
              placeholder="Search TPAs"
              class="cv-scope__select"
              @input="set('tpas', $event)"
            >
              <el-option v-for="name in tpaOptions" :key="name" :label="name" :value="name" />
            </el-select>
          </template>
          <div v-if="errorOf(['tpas'])" class="cv-scope__error">{{ errorOf(['tpas']) }}</div>
        </section>
      </b-col>
    </b-form-row>

    <!-- The scope check: choices that can never take effect, each with why. -->
    <div v-if="warnings.length" class="cv-scope__warning" role="alert">
      <b-icon icon="exclamation-circle-fill" class="cv-scope__warning-icon" />
      <div class="flex-grow-1">
        <p class="mb-1">
          <strong>{{ warnings.length === 1 ? 'This scope contains a contradiction.' : `This scope contains ${warnings.length} contradictions.` }}</strong>
          <template v-if="warnings.length === 1"> {{ warnings[0].message }}</template>
        </p>
        <ul v-if="warnings.length > 1" class="mb-1 pl-3">
          <li v-for="(warning, index) in warnings" :key="index">{{ warning.message }}</li>
        </ul>
        <div class="cv-scope__warning-actions">
          <b-button variant="link" size="sm" class="p-0" @click="review">Review scope</b-button>
          <b-form-checkbox :checked="override" @change="$emit('update:override', $event)">
            Override and save as set
          </b-form-checkbox>
        </div>
        <p v-if="blocked && !override" class="small mb-0 mt-1">
          Saving is blocked until you change the scope or tick “Override and save as set”.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option } from 'element-ui'
import CvSpinner from './CvSpinner.vue'

const MAX_UMR_MATCHES = 50

// The "Applies to" tiles of the rule builder (LIQ-2716): Programs (chosen by UMR, with each UMR's
// LimeIQ keys in an accordion to tick or untick), Jurisdictions and TPAs - each All / Only these /
// All except - and the scope check's warnings with "Review scope" and "Override and save as set".
// value is the builder's scope: { programsMode, umrs, keyExceptions, jurisdictionsMode,
// jurisdictions, tpasMode, tpas } (modes 'all' | 'only' | 'except'); every change emits a new one.
// A key in keyExceptions is ticked the other way from its UMR: left out by "Only these", kept in by
// "All except".
export default {
  name: 'RuleScopeTiles',

  components: {
    CvSpinner,
    [Select.name]: Select,
    [Option.name]: Option
  },

  props: {
    value: {
      type: Object,
      required: true
    },
    // The programs the company leads: [{ key, umr, year_of_account, jurisdiction, tpa }].
    programs: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    jurisdictionOptions: {
      type: Array,
      default: () => []
    },
    tpaOptions: {
      type: Array,
      default: () => []
    },
    companyName: {
      type: String,
      default: ''
    },
    // The builder's field errors (API field -> message).
    errors: {
      type: Object,
      default: () => ({})
    },
    // The scope check's warnings: [{ part, item, message }].
    warnings: {
      type: Array,
      default: () => []
    },
    override: {
      type: Boolean,
      default: false
    },
    // A save was refused for these warnings.
    blocked: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      umrMatches: [],
      adding: null, // the UMR search box - cleared once a UMR is added
      openUmr: null, // the chosen UMR whose keys are showing (one at a time)
      modeOptions: [
        { value: 'all', text: 'All' },
        { value: 'only', text: 'Only these' },
        { value: 'except', text: 'All except' }
      ]
    }
  },

  computed: {
    // UMR -> its programs, sorted by key; UMRs in order.
    umrGroups() {
      const groups = {}
      this.programs.forEach((program) => {
        const umr = String(program.umr || '').trim().toUpperCase()
        if (!umr) return
        if (!groups[umr]) groups[umr] = { umr, programs: [] }
        groups[umr].programs.push(program)
      })
      return Object.values(groups)
        .map((group) => ({ ...group, programs: group.programs.slice().sort((a, b) => a.key.localeCompare(b.key)) }))
        .sort((a, b) => a.umr.localeCompare(b.umr))
    },
    umrIndex() {
      return Object.fromEntries(this.umrGroups.map((group) => [group.umr, group]))
    },
    warnedParts() {
      const parts = new Set(this.warnings.map((warning) => warning.part))
      if (parts.has('scope')) ['programs', 'jurisdictions', 'tpas'].forEach((part) => parts.add(part))
      return parts
    }
  },

  methods: {
    set(field, value) {
      this.$emit('input', { ...this.value, [field]: value })
    },

    // Removing a UMR drops its keys' exceptions; a newly added UMR opens to show its keys.
    setUmrs(umrs) {
      const kept = new Set(umrs)
      const keyExceptions = this.value.keyExceptions.filter((key) => {
        const program = this.programs.find((p) => p.key === key)
        return program && kept.has(String(program.umr || '').trim().toUpperCase())
      })
      const added = umrs.find((umr) => !this.value.umrs.includes(umr))
      if (added) this.openUmr = added
      this.$emit('input', { ...this.value, umrs, keyExceptions })
    },

    addUmr(umr) {
      if (umr && !this.value.umrs.includes(umr)) this.setUmrs([...this.value.umrs, umr])
      this.$nextTick(() => {
        this.adding = null
      })
    },

    removeUmr(umr) {
      if (this.openUmr === umr) this.openUmr = null
      this.setUmrs(this.value.umrs.filter((chosen) => chosen !== umr))
    },

    // Thousands of programs: only the first matching UMRs are offered as the user types. A key
    // search finds the UMR it's under.
    searchUmrs(query) {
      const wanted = String(query || '').trim().toLowerCase()
      this.umrMatches = this.umrGroups
        .filter(
          (group) =>
            !wanted ||
            group.umr.toLowerCase().includes(wanted) ||
            group.programs.some((program) => program.key.toLowerCase().includes(wanted))
        )
        .slice(0, MAX_UMR_MATCHES)
    },

    programsOf(umr) {
      return (this.umrIndex[umr] && this.umrIndex[umr].programs) || []
    },

    isOpen(umr) {
      return this.openUmr === umr
    },

    toggleOpen(umr) {
      this.openUmr = this.isOpen(umr) ? null : umr
    },

    // A key under its UMR's heading, without repeating the UMR: "…_B3_17F_0_USD_0_MIN-362".
    shortKey(key, umr) {
      return key.toUpperCase().startsWith(`${umr}_`) ? `…${key.slice(umr.length)}` : key
    },

    isException(key) {
      return this.value.keyExceptions.includes(key)
    },

    setKeyTicked(key, ticked) {
      const others = this.value.keyExceptions.filter((existing) => existing !== key)
      this.$emit('input', { ...this.value, keyExceptions: ticked ? others : [...others, key] })
    },

    keysLine(umr) {
      const all = this.programsOf(umr)
      const ticked = all.filter((program) => !this.isException(program.key)).length
      const what = this.value.programsMode === 'only' ? 'checked' : 'left out'
      return ticked === all.length ? `all ${this.plural(all.length, 'key')} ${what}` : `${ticked} of ${all.length} keys ${what}`
    },

    programDetails(program) {
      return [program.year_of_account, program.jurisdiction, program.tpa].filter(Boolean).join(' · ')
    },

    plural(count, word) {
      return `${count} ${word}${count === 1 ? '' : 's'}`
    },

    errorOf(fields) {
      const field = fields.find((name) => this.errors[name])
      return field ? this.errors[field] : null
    },

    tileClass(part, fields) {
      return {
        'cv-scope__tile--invalid': !!this.errorOf(fields),
        'cv-scope__tile--warned': this.warnedParts.has(part)
      }
    },

    // Takes the user to the first tile a warning is about.
    review() {
      const first = ['programs', 'jurisdictions', 'tpas'].find((part) => this.warnedParts.has(part))
      const tile = first && this.$refs[first]
      if (!tile) return
      if (tile.scrollIntoView) tile.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      tile.focus()
    }
  }
}
</script>

<style>
.cv-scope__tile {
  height: 100%;
  padding: 12px 14px;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #ffffff;
}

.cv-scope__tile:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(67, 83, 201, 0.2);
}

.cv-scope__tile--warned {
  border-color: #e3a33b;
}

.cv-scope__tile--invalid {
  border-color: #b42318;
}

.cv-scope__title {
  margin-bottom: 8px;
  font-size: 0.95rem;
  font-weight: bold;
  color: #1f2a44;
}

.cv-scope__modes {
  display: flex;
  width: 100%;
}

.cv-scope__modes .btn {
  flex: 1 1 0;
  white-space: nowrap;
}

.cv-scope__select {
  width: 100%;
}

.cv-scope__note {
  margin-left: 8px;
  color: #6b778c;
  font-size: 0.75rem;
}

.cv-scope__mono {
  font-family: 'JetBrains Mono', SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.8rem;
}

/* The chosen UMRs: a list of fixed height, so many UMRs (or one with 200 keys) don't stretch the
   tiles - it scrolls instead. */
.cv-scope__chosen {
  max-height: 260px;
  margin-top: 8px;
  overflow-y: auto;
  border: 1px solid #dfe3ec;
  border-radius: 6px;
}

.cv-scope__umr + .cv-scope__umr {
  border-top: 1px solid #eef0f5;
}

.cv-scope__umr-row {
  display: flex;
  align-items: center;
}

.cv-scope__umr-toggle {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  padding: 6px 8px;
  text-align: left;
  color: #1f2a44;
  background: none;
  border: 0;
  cursor: pointer;
}

.cv-scope__remove {
  flex: none;
  padding: 4px 8px;
  color: #6b778c;
  background: none;
  border: 0;
  cursor: pointer;
}

.cv-scope__remove:hover {
  color: #b42318;
}

.cv-scope__keys {
  padding: 4px 8px 6px 26px;
  background: #fafbfe;
}

.cv-scope__key {
  margin-bottom: 2px;
}

/* Long keys wrap rather than scroll sideways. */
.cv-scope__key .custom-control-label {
  overflow-wrap: anywhere;
}

.cv-scope__error {
  margin-top: 6px;
  font-size: 80%;
  color: #b42318;
}

.cv-scope__warning {
  display: flex;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
  font-size: 0.875rem;
  color: #5c3b00;
  background: #fff8e6;
  border: 1px solid #e3a33b;
  border-radius: 8px;
}

.cv-scope__warning-icon {
  flex: none;
  margin-top: 3px;
  color: #b7791f;
}

.cv-scope__warning-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}
</style>
